import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Bell, Check, Heart, MessageCircle, UserPlus, ShieldCheck, Megaphone, Award, TrendingUp } from "lucide-react";
import { syncCivicProgress } from "@/lib/civic-progress";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/notifications")({
  head: () => ({
    meta: [
      { title: "Xamseen — Notifications" },
      { name: "description", content: "Replies, likes, follows and official responses on your civic reports." },
      { property: "og:title", content: "Notifications — Xamseen" },
      { property: "og:description", content: "Replies, likes, follows and official responses on your civic reports." },
    ],
  }),
  component: NotificationsPage,
});

type Notif = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read: boolean;
  created_at: string;
  actor_id: string | null;
  actor?: { username: string; full_name: string | null; avatar_url: string | null } | null;
};

type Filter = "all" | "unread" | "mentions" | "reports" | "progress";

const ICONS: Record<string, typeof Bell> = {
  comment: MessageCircle,
  reply: MessageCircle,
  answer: MessageCircle,
  solution_accepted: ShieldCheck,
  reaction: Heart,
  follow: UserPlus,
  community_update: UserPlus,
  community_invite: UserPlus,
  report_status: TrendingUp,
  report_verified: ShieldCheck,
  report_resolved: ShieldCheck,
  project_update: TrendingUp,
  security: ShieldCheck,
  system: TrendingUp,
  achievement: Award,
  verification: ShieldCheck,
  emergency_alert: Megaphone,
};

function NotificationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<Notif[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("notifications")
      .select("id, type, title, body, link, read, created_at, actor_id, actor:profiles!notifications_actor_profile_fkey ( username, full_name, avatar_url )")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(100);
    setItems((data ?? []) as unknown as Notif[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  // Award badges / record radar improvements, then pick up any new notifications.
  useEffect(() => {
    if (!user) return;
    (async () => {
      const created = await syncCivicProgress();
      if (created > 0) await load();
    })();
  }, [user, load]);

  // Live updates
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`notif-page-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => setItems((prev) => [payload.new as unknown as Notif, ...prev]),
      )
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [user]);

  const markAll = async () => {
    if (!user) return;
    await supabase.from("notifications").update({ read: true }).eq("user_id", user.id).eq("read", false);
    setItems((it) => it.map((i) => ({ ...i, read: true })));
  };

  const markOne = async (id: string) => {
    setItems((it) => it.map((i) => (i.id === id ? { ...i, read: true } : i)));
    await supabase.from("notifications").update({ read: true }).eq("id", id);
  };

  if (!user) {
    return (
      <AppShell>
        <Card className="mx-auto max-w-md p-10 text-center">
          <Bell className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">Sign in to see notifications</p>
          <Button asChild className="mt-4"><Link to="/auth">Sign in</Link></Button>
        </Card>
      </AppShell>
    );
  }

  const REPORT_TYPES = ["report_status", "report_verified", "report_resolved"];
  const shown = items.filter((n) =>
    filter === "unread"
      ? !n.read
      : filter === "mentions"
        ? n.type === "mention" || n.type === "reply" || n.type === "answer" || n.type === "comment"
        : filter === "reports"
          ? REPORT_TYPES.includes(n.type)
          : filter === "progress"
            ? n.type === "achievement" || n.type === "system"
            : true,
  );
  const unreadCount = items.filter((n) => !n.read).length;

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="font-display text-2xl font-semibold">Notifications</h1>
          <Button variant="outline" size="sm" onClick={markAll} disabled={unreadCount === 0}>
            <Check className="mr-2 h-4 w-4" />Mark all read
          </Button>
        </div>

        <div className="mb-4 flex gap-1 rounded-full bg-muted p-1">
          {([["all", "All"], ["unread", `Unread${unreadCount ? ` (${unreadCount})` : ""}`], ["mentions", "Replies"], ["reports", "Reports"], ["progress", "Progress"]] as const).map(
            ([key, label]) => (
              <button key={key} onClick={() => setFilter(key as Filter)}
                className={cn(
                  "flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                  filter === key ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}>
                {label}
              </button>
            ),
          )}
        </div>

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />)}
          </div>
        ) : shown.length === 0 ? (
          <Card className="p-12 text-center">
            <Bell className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">You're all caught up</p>
            <p className="text-sm text-muted-foreground">
              {filter === "progress"
                ? "Earn a badge or grow an impact area and it shows up here, linked to the post or space behind it."
                : "New replies, follows, alerts and badge milestones will appear here instantly."}
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {shown.map((n) => {
              const Icon = ICONS[n.type] ?? Bell;
              const inner = (
                <Card className={cn("p-4 transition-colors hover:bg-accent/40", !n.read && "border-primary/40 bg-primary/5")}>
                  <div className="flex items-start gap-3">
                    {n.actor ? (
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarImage src={n.actor.avatar_url ?? undefined} />
                        <AvatarFallback>{(n.actor.full_name ?? n.actor.username ?? "U")[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-muted">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate font-medium">{n.title}</span>
                      </div>
                      {n.body && <div className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{n.body}</div>}
                      <div className="mt-1 text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </div>
                    </div>
                    {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </div>
                </Card>
              );
              return n.link ? (
                <a key={n.id} href={n.link} onClick={() => markOne(n.id)} className="block">{inner}</a>
              ) : (
                <button key={n.id} onClick={() => markOne(n.id)} className="block w-full text-left">{inner}</button>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
